/**
 * Pure helpers for <AcceptInvitation /> (Task 17 / #49).
 *
 * Kept in their own module so they can be unit-tested in vitest's
 * `node` environment without pulling in React or react-router.
 *
 * The companion JSX component (`AcceptInvitation.jsx`) imports from
 * this file. Mirrors the Task 16 `tenantSwitcherHelpers.js` layout.
 */
import { decodeBase64Url } from '../../utils/base64url'

/**
 * localStorage key used to remember a return URL for after login.
 *
 * Both <AcceptInvitation /> (writer, for logged-out invitees) and
 * <LoginPage /> (reader, on successful login) reference this exact
 * key. If you rename it, update both — the test
 * `POST_LOGIN_REDIRECT_KEY` exposes the canonical name to catch
 * accidental drift.
 *
 * NOTE: there was no general post-login redirect plumbing in the
 * codebase before Task 17 — only the more specific `pendingPlan`
 * billing flow. Rather than retrofit a query-param approach
 * (`?postLogin=...`) we picked the same localStorage pattern the
 * billing flow already uses, since it survives Cognito's OAuth/SSO
 * redirect round-trip cleanly.
 */
export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'

/**
 * Decode a base64url-encoded JSON invite blob into its
 * `{ u, t, k }` shape.
 *
 * The invite blob is built server-side by `buildInviteAcceptUrl`
 * (see `infrastructure/lambda/shared/invite-payload.ts`):
 *
 *   invite = base64url(JSON.stringify({ u: userId, t: tenantId, k: token }))
 *
 * SECURITY: this helper ONLY parses the blob — it does NOT validate
 * the `k` token. Validation happens server-side, in the lambda
 * handler, against the DynamoDB row's stored `inviteToken`. The
 * client-side parse is purely so the UI can decide what to render
 * (button vs. malformed-link error).
 *
 * @param {unknown} inviteParam
 * @returns {{u: string, t: string, k: string} | null}
 */
export function parseInvitePayload(inviteParam) {
  if (typeof inviteParam !== 'string' || inviteParam.length === 0) {
    return null
  }
  let json
  try {
    // Buffer is a Node global — it does not exist in the browser and
    // Vite does not polyfill it, so decoding must go through atob (see
    // decodeBase64Url in utils/base64url.js).
    json = decodeBase64Url(inviteParam)
  } catch {
    return null
  }
  let payload
  try {
    payload = JSON.parse(json)
  } catch {
    return null
  }
  if (!payload || typeof payload !== 'object') {
    return null
  }
  const { u, t, k } = payload
  if (
    typeof u !== 'string' ||
    typeof t !== 'string' ||
    typeof k !== 'string' ||
    !u ||
    !t ||
    !k
  ) {
    return null
  }
  return { u, t, k }
}

/**
 * Build the post-login return URL for a given invite blob.
 *
 * Used by the logged-out branch of <AcceptInvitation />: we stash
 * this URL under POST_LOGIN_REDIRECT_KEY, then send the user to
 * `/login`. After login, LoginPage reads the key and navigates back
 * here so the user lands on the accept-invite page again — this time
 * with a session attached.
 *
 * The invite blob is URL-encoded once so the recipient (LoginPage)
 * sees the original base64url string when it parses the URL.
 *
 * @param {unknown} inviteParam
 * @returns {string | null}
 */
export function buildPostLoginReturnUrl(inviteParam) {
  if (typeof inviteParam !== 'string' || inviteParam.length === 0) {
    return null
  }
  return `/invitations/accept?invite=${encodeURIComponent(inviteParam)}`
}
